// Version: 3.6
// v3.6: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v3.5: Website Analytics Report has a Download button: saves the report for the chosen period as an HTML file without emailing anyone.
// v3.4: Action rows wrap via NewAssessment.css (one rule for all three report types).
// v3.3: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v3.2: Download buttons beside Open latest report on the IT and Cyber Security
//       Report form: the client version and, for admins, the internal one.
// v3.1: ?type=...&for=<slug> opens a report type with the client already
//       chosen (the Generate button on every report section). Emailing a
//       report goes through the same confirm box as everywhere else
//       (ReportSend.js): IT reports get Email to client after generating,
//       and analytics Send now became Email to client.
// v3.0: Reports and Assessments. The page now offers the three agreed outputs:
//       IT and Cyber Security Report (pick a client, see which data it will
//       draw on and when it last updated, generate without emailing, open
//       it), Website Analytics Report (pick a tracked client, preview, send
//       now or send a test; scheduling lives on the Schedules page) and
//       External Cyber Security Assessment (existing client with domains
//       pre-filled and the office connections that will be port scanned
//       listed, or a new prospect as before). Existing clients start through
//       /api/clients/<slug>/assessment/run. ?type=it_report|analytics|
//       assessment preselects an option; ?slug=&client= still resumes a scan.
//       Client users still see only their own assessment form.
// v2.3: VPN badge on the admin progress view (connecting / connected /
//        reconnecting), read from the scan log. Added the m365recon stage
//        ("Productivity Suite Check") so it shows a progress bar like the
//        other tools.
import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authFetch, useAuth, getSseToken } from './auth';
import { SendReportDialog } from './ReportSend';
import './NewAssessment.css';

// ── Smooth progress hook ──────────────────────────────────────────────────────
function useSmoothProgress(targetPct, done) {
  const [displayPct, setDisplayPct] = React.useState(0);
  const displayRef = React.useRef(0);
  const rafRef = React.useRef(null);

  React.useEffect(() => {
    if (done) { setDisplayPct(100); return; }
    const target = targetPct;
    const animate = () => {
      const current = displayRef.current;
      if (current < target) {
        const step = Math.min(0.3, Math.max(0.05, (target - current) * 0.015));
        displayRef.current = Math.min(target, current + step);
        setDisplayPct(Math.round(displayRef.current * 10) / 10);
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [targetPct, done]);

  React.useEffect(() => {
    if (done) return;
    const interval = setInterval(() => {
      if (displayRef.current < 95 && displayRef.current >= targetPct - 1) {
        displayRef.current += 0.03;
        setDisplayPct(Math.round(displayRef.current * 10) / 10);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [targetPct, done]);

  return done ? 100 : displayPct;
}

// ── Stage definitions ─────────────────────────────────────────────────────────

const ADMIN_STAGES = [
  { key: 'harvester',    label: 'Reconnaissance',          match: /harvester|theHarvester|\[\*\].*pull/i,               weight: 18 },
  { key: 'testssl',      label: 'SSL and TLS Assessment',   match: /testssl|Testing.*SSL|ssl.*scan/i,                    weight: 16 },
  { key: 'm365recon',    label: 'Productivity Suite Check',  match: /m365recon|M365Recon/i,                               weight: 4  },
  { key: 'dns',          label: 'DNS Analysis',             match: /dns.*check|dns-check|dnspython|resolv/i,             weight: 7  },
  { key: 'abuseipdb',   label: 'Abuse and Reputation',     match: /abuseipdb|abuse.*ip/i,                               weight: 5  },
  { key: 'osint',        label: 'Reputation Intelligence',  match: /osint|dork|serper|virustotal/i,                      weight: 7  },
  { key: 'shodan',       label: 'Internet Exposure',        match: /shodan/i,                                            weight: 5  },
  { key: 'headers',      label: 'Headers Analysis',         match: /header.*check|security.*header/i,                   weight: 4  },
  { key: 'cookies',      label: 'Cookie Compliance',        match: /\[Cookies\]|cookie.*compliance|cookie.*scan/i,      weight: 4  },
  { key: 'blacklist',    label: 'Blacklist Check',          match: /blacklist|dnsbl|spamhaus/i,                          weight: 4  },
  { key: 'tldsweep',     label: 'Brand Sweep',              match: /tldsweep|tld.*sweep|brand.*sweep/i,                 weight: 4  },
  { key: 'wpscan',       label: 'WordPress Scan',           match: /wpscan|wordpress/i,                                  weight: 4  },
  { key: 'knockpy',      label: 'Subdomain Enumeration',    match: /knockpy|subdomain/i,                                 weight: 4  },
  { key: 'nikto',        label: 'Web Server Scan',          match: /nikto/i,                                             weight: 4  },
  { key: 'rustscan',     label: 'Port Scan',                match: /rustscan|open port|live.*host/i,                    weight: 4  },
  { key: 'baddns',       label: 'Takeover Check',           match: /baddns|takeover|dangling/i,                         weight: 4  },
  { key: 'nuclei',       label: 'Vulnerability Scan',       match: /nuclei|vuln.*template/i,                            weight: 5  },
  { key: 'buckets',      label: 'Cloud Storage',            match: /bucket.*check|s3|cloud.*storage/i,                  weight: 4  },
  { key: 'credexposure', label: 'Credential Exposure',      match: /credexposure|exposed.*file|sensitive.*file/i,       weight: 4  },
  { key: 'dbexposure',   label: 'Database Exposure',        match: /dbexposure|database.*port|unauthenticated.*db/i,    weight: 4  },
  { key: 'hudsonrock',   label: 'Infostealer Exposure',     match: /hudsonrock|infostealer|cavalier/i,                  weight: 3  },
  { key: 'reporter',     label: 'Generating Report',        match: /generate.*report|report.*assess/i,                  weight: 5  },
];

const TOTAL_WEIGHT = ADMIN_STAGES.reduce((s, st) => s + st.weight, 0);
let cumulative = 0;
ADMIN_STAGES.forEach(st => {
  st.startPct = Math.round((cumulative / TOTAL_WEIGHT) * 100);
  cumulative += st.weight;
  st.endPct = Math.round((cumulative / TOTAL_WEIGHT) * 100);
});

const CLIENT_STAGES = [
  { key: 'recon',   label: 'Gathering information',              keys: ['harvester','dns'] },
  { key: 'ssl',     label: 'Checking security certificates',     keys: ['testssl'] },
  { key: 'threats', label: 'Analysing threats and reputation',   keys: ['abuseipdb','osint','shodan','blacklist'] },
  { key: 'vulns',   label: 'Scanning for vulnerabilities',       keys: ['headers','cookies','tldsweep','wpscan','knockpy','nikto','rustscan','baddns','nuclei','buckets','credexposure','dbexposure','hudsonrock'] },
  { key: 'report',  label: 'Generating your report',            keys: ['reporter'] },
];

function getAdminStageStatuses(lines) {
  const joined = lines.join('\n');
  let lastMatchedIdx = -1;
  ADMIN_STAGES.forEach((stage, i) => {
    if (stage.match.test(joined)) lastMatchedIdx = i;
  });
  const statuses = {};
  ADMIN_STAGES.forEach((stage, i) => {
    if (i < lastMatchedIdx) statuses[stage.key] = 'done';
    else if (i === lastMatchedIdx) statuses[stage.key] = 'active';
    else statuses[stage.key] = 'pending';
  });
  return statuses;
}

function getClientStageStatuses(adminStatuses) {
  return CLIENT_STAGES.map(stage => {
    const allDone  = stage.keys.every(k => adminStatuses[k] === 'done');
    const anyActive = stage.keys.some(k => adminStatuses[k] === 'active');
    const anyDone   = stage.keys.some(k => adminStatuses[k] === 'done' || adminStatuses[k] === 'active');
    if (allDone) return { ...stage, status: 'done' };
    if (anyActive || anyDone) return { ...stage, status: 'active' };
    return { ...stage, status: 'pending' };
  });
}

// ── Per-tool progress system ──────────────────────────────────────────────────

const HARVESTER_DEPENDENT = new Set([
  'm365recon','dns','abuseipdb','osint','shodan','headers','cookies','blacklist',
  'tldsweep','wpscan','knockpy','nikto','rustscan','baddns',
  'nuclei','buckets','credexposure','dbexposure','hudsonrock'
]);

const TOOL_START_PATTERNS = {
  harvester:    /\[theHarvester\].*[Ss]canning domain|\[theHarvester\].*[Ss]tarting/i,
  testssl:      /\[testssl\].*[Cc]hecking domain|\[testssl\].*[Ss]tarting/i,
  m365recon:    /\[M365Recon\]/i,
  dns:          /\[DNS\]/i,
  abuseipdb:    /\[AbuseIPDB\]/i,
  osint:        /\[OSINT\]/i,
  shodan:       /\[Shodan\]/i,
  headers:      /\[Headers\]/i,
  cookies:      /\[Cookies\]/i,
  blacklist:    /\[Blacklist\]/i,
  tldsweep:     /\[TLDSweep\]/i,
  wpscan:       /\[WPScan\]/i,
  knockpy:      /\[Knockpy\]/i,
  nikto:        /\[Nikto\]/i,
  rustscan:     /\[rustscan\]/i,
  baddns:       /\[BadDNS\]|\[baddns\]/i,
  nuclei:       /\[Nuclei\]/i,
  buckets:      /\[Buckets\]/i,
  credexposure: /\[CredExposure\]/i,
  dbexposure:   /\[DBExposure\]/i,
  hudsonrock:   /\[HudsonRock\]/i,
  reporter:     /\[Assessment\].*[Gg]enerating|\[Assessment\].*[Rr]eport saved/i,
};

const TOOL_PROGRESS_PATTERNS = {
  harvester: /\[theHarvester\].*[Ss]canning domain (\d+)\/(\d+)/i,
  testssl:   /\[testssl\].*[Cc]hecking domain (\d+)\/(\d+)/i,
  tldsweep:  /\[TLDSweep\].*[Ss]weeping brand (\d+)\/(\d+)/i,
  cookies:   /\[Cookies\].*[Ss]canning target (\d+)\/(\d+)/i,
  wpscan:    /\[WPScan\].*[Ss]canning target (\d+)\/(\d+)/i,
  knockpy:   /\[Knockpy\].*(\d+)\/(\d+) words checked/i,
  rustscan:  /\[rustscan\].*[Ss]canning IP (\d+)\/(\d+)/i,
};

const TOOL_COMPLETE_PATTERNS = {
  harvester:    /\[Assessment\].*[Hh]arvester.*complete|\[theHarvester\].*[Aa]ll domains complete/i,
  testssl:      /\[Assessment\].*testssl.*complete|\[testssl\].*[Aa]ll SSL checks complete/i,
  m365recon:    /\[Assessment\].*m365recon.*complete|\[M365Recon\].*Done/i,
  dns:          /\[Assessment\].*DNS.*complete|\[DNS\].*[Cc]omplete/i,
  abuseipdb:    /\[Assessment\].*AbuseIPDB.*complete|\[AbuseIPDB\].*[Cc]omplete/i,
  osint:        /\[Assessment\].*OSINT.*complete|\[OSINT\].*[Cc]omplete/i,
  shodan:       /\[Assessment\].*Shodan.*complete|\[Shodan\].*[Cc]omplete/i,
  headers:      /\[Assessment\].*[Hh]eaders.*complete|\[Headers\].*[Cc]omplete/i,
  cookies:      /\[Assessment\].*[Cc]ookies.*complete|\[Cookies\].*[Cc]omplete/i,
  blacklist:    /\[Assessment\].*[Bb]lacklist.*complete|\[Blacklist\].*[Cc]omplete/i,
  tldsweep:     /\[Assessment\].*TLDSweep.*complete|\[TLDSweep\].*[Cc]omplete/i,
  wpscan:       /\[Assessment\].*WPScan.*complete|\[WPScan\].*[Cc]omplete/i,
  knockpy:      /\[Assessment\].*Knockpy.*complete|\[Knockpy\].*[Tt]otal/i,
  nikto:        /\[Assessment\].*Nikto.*complete|\[Nikto\].*[Cc]omplete/i,
  rustscan:     /\[Assessment\].*RustScan.*complete|\[rustscan\].*[Cc]omplete/i,
  baddns:       /\[Assessment\].*BadDNS.*complete|\[BadDNS\].*[Cc]omplete/i,
  nuclei:       /\[Assessment\].*Nuclei.*complete|\[Nuclei\].*[Cc]omplete/i,
  buckets:      /\[Assessment\].*[Bb]ucket.*complete|\[Buckets\].*[Cc]omplete/i,
  credexposure: /\[Assessment\].*[Cc]redential.*complete|\[CredExposure\].*[Cc]omplete/i,
  dbexposure:   /\[Assessment\].*[Dd]atabase.*complete|\[DBExposure\].*[Cc]omplete/i,
  hudsonrock:   /\[Assessment\].*HudsonRock.*complete|\[HudsonRock\].*[Ee]xiting cleanly/i,
  reporter:     /\[Assessment\].*Grade:/i,
};

function parseToolProgress(lines) {
  const joined = lines.join('\n');
  const harvesterDone = TOOL_COMPLETE_PATTERNS['harvester'].test(joined);
  const rustscanDone  = TOOL_COMPLETE_PATTERNS['rustscan'].test(joined);
  const state = {};
  ADMIN_STAGES.forEach(({ key }) => {
    const started   = TOOL_START_PATTERNS[key]    ? TOOL_START_PATTERNS[key].test(joined)    : false;
    const completed = TOOL_COMPLETE_PATTERNS[key] ? TOOL_COMPLETE_PATTERNS[key].test(joined) : false;
    let pct = 0, status = 'queued';
    if (completed) {
      pct = 100; status = 'done';
    } else if (started) {
      status = 'running';
      const prog = TOOL_PROGRESS_PATTERNS[key];
      if (prog) {
        let lastX = null, lastY = null;
        lines.forEach(line => {
          const m = line.match(prog);
          if (m) { lastX = parseInt(m[1]); lastY = parseInt(m[2]); }
        });
        pct = (lastX !== null && lastY > 0) ? Math.round((lastX / lastY) * 95) : 5;
      } else { pct = 5; }
    }
    if (HARVESTER_DEPENDENT.has(key) && !harvesterDone && status === 'queued') status = 'waiting';
    if (['nuclei','dbexposure'].includes(key) && !rustscanDone && status !== 'done') {
      if (status !== 'running') status = 'waiting';
    }
    state[key] = { status, pct };
  });
  return state;
}

function ToolBar({ label, status, pct, maxCreep }) {
  const displayRef = React.useRef(0);
  const [displayPct, setDisplayPct] = useState(0);
  const rafRef = React.useRef(null);

  useEffect(() => {
    const target = status === 'done' ? 100 : pct;
    if (target <= displayRef.current && status !== 'done') return;
    const animate = () => {
      displayRef.current = Math.min(target, displayRef.current + Math.min(0.5, Math.max(0.05, (target - displayRef.current) * 0.05)));
      setDisplayPct(Math.round(displayRef.current * 10) / 10);
      if (displayRef.current < target) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [pct, status]);

  useEffect(() => {
    if (status !== 'running') return;
    const interval = setInterval(() => {
      if (displayRef.current < (maxCreep || 90)) {
        displayRef.current += 0.04;
        setDisplayPct(Math.round(displayRef.current * 10) / 10);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [status, maxCreep]);

  const barColor  = status === 'done' ? '#30d158' : status === 'running' ? 'var(--accent)' : '#2a2a2e';
  const textColor = status === 'done' ? '#30d158' : status === 'running' ? 'var(--accent)' : '#555';
  const icon      = status === 'done' ? '✓' : status === 'running' ? '●' : '○';

  return (
    <div style={{marginBottom:7}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:2}}>
        <span style={{fontSize:11,color:textColor,fontWeight:status==='running'?600:400}}>
          {icon} {label}
        </span>
        <span style={{fontSize:10,color:textColor,fontFamily:'var(--font-mono)'}}>
          {status==='waiting'?'waiting':status==='queued'?'—':`${Math.round(status==='done'?100:displayPct)}%`}
        </span>
      </div>
      <div style={{height:3,background:'#2a2a2e',borderRadius:2,overflow:'hidden'}}>
        <div style={{
          height:'100%', width:`${status==='done'?100:displayPct}%`,
          background:barColor, borderRadius:2,
          boxShadow:status==='running'?`0 0 4px ${barColor}`:'none',
          transition:status==='done'?'width 0.3s ease':'none'
        }} />
      </div>
    </div>
  );
}

// ── Progress views ────────────────────────────────────────────────────────────

function vpnStateFromLines(lines) {
  let state = null;
  for (const l of lines) {
    if (/VPN:\s*off/i.test(l))                     state = 'off';
    else if (/VPN:\s*on/i.test(l))                 state = 'connecting';
    else if (/Connecting VPN/i.test(l))            state = 'connecting';
    else if (/VPN connected/i.test(l))             state = 'connected';
    else if (/VPN unhealthy|VPN dropped/i.test(l)) state = 'reconnecting';
    else if (/Restarting assessment/i.test(l) && state && state !== 'off') state = 'connecting';
  }
  return state;
}
const VPN_META = {
  connecting:   { label: 'VPN connecting',   color: '#ff9f0a', pulse: true  },
  connected:    { label: 'VPN connected',    color: '#30d158', pulse: false },
  reconnecting: { label: 'VPN reconnecting', color: '#ff2d55', pulse: true  },
  off:          { label: 'No VPN',           color: '#86868b', pulse: false },
};
function VpnBadge({ state }) {
  if (!state || !VPN_META[state]) return null;
  const m = VPN_META[state];
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:5,padding:'2px 8px',borderRadius:20,
      background:`${m.color}1f`,color:m.color,fontSize:10,fontWeight:700,letterSpacing:'0.04em',marginLeft:8}}>
      <style>{`@keyframes vpnpulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
      <span style={{width:6,height:6,borderRadius:'50%',background:m.color,animation:m.pulse?'vpnpulse 1.2s ease-in-out infinite':'none'}} />
      {m.label}
    </span>
  );
}

function AdminProgress({ lines, done, clientName }) {
  const toolState = parseToolProgress(lines);
  const completedCount = ADMIN_STAGES.filter(s => toolState[s.key]?.status === 'done').length;
  const overallPct = done ? 100 : Math.round((completedCount / ADMIN_STAGES.length) * 100);
  const pct = useSmoothProgress(overallPct, done);

  return (
    <div className="progress-wrap">
      <div className="progress-header">
        <div className="progress-title">
          {done ? <><span className="done-dot" /> Completed — {clientName}</> : <><span className="pulse-dot" /> Running — {clientName}</>}
          {!done && <VpnBadge state={vpnStateFromLines(lines)} />}
        </div>
        <div className="progress-pct">{Math.round(pct)}%</div>
      </div>
      <div className="progress-bar-track" style={{marginBottom:16}}>
        <div className="progress-bar-fill" style={{width:`${Math.round(pct)}%`}} />
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 24px'}}>
        {ADMIN_STAGES.map(stage => (
          <ToolBar
            key={stage.key}
            label={stage.label}
            status={done ? 'done' : (toolState[stage.key]?.status || 'queued')}
            pct={done ? 100 : (toolState[stage.key]?.pct || 0)}
            maxCreep={stage.key === 'reporter' ? 30 : 90}
          />
        ))}
      </div>
    </div>
  );
}

function ClientProgress({ lines, done, clientName, slug }) {
  const adminStatuses = getAdminStageStatuses(lines);
  const clientStages  = getClientStageStatuses(adminStatuses);
  const activeClientIdx    = clientStages.findIndex(s => s.status === 'active');
  const lastDoneClientIdx  = clientStages.reduce((acc, s, i) => s.status === 'done' ? i : acc, -1);
  let targetPct = 0;
  if (done) { targetPct = 100; }
  else if (activeClientIdx >= 0) { targetPct = Math.round((activeClientIdx / CLIENT_STAGES.length) * 90) + 5; }
  else if (lastDoneClientIdx >= 0) { targetPct = Math.round(((lastDoneClientIdx + 1) / CLIENT_STAGES.length) * 90); }
  else if (lines.length > 0) { targetPct = 3; }
  const pct = useSmoothProgress(targetPct, done);
  const activeStage = clientStages.find(s => s.status === 'active');

  return (
    <div className="progress-wrap client-progress">
      <div className="progress-header">
        <div className="progress-title">
          {done ? 'Assessment complete' : (activeStage?.label || 'Starting assessment...')}
        </div>
        <div className="progress-pct">{pct}%</div>
      </div>
      <div className="progress-bar-track">
        <div className="progress-bar-fill" style={{width:`${pct}%`}} />
      </div>
      <div className="client-stages">
        {clientStages.map((stage, i) => (
          <div key={stage.key} className={`client-stage client-stage-${done ? 'done' : stage.status}`}>
            <div className="client-stage-num">
              {(done || stage.status === 'done') ? '✓' : stage.status === 'active' ? <span className="mini-pulse" /> : i + 1}
            </div>
            <div className="client-stage-label">{stage.label}</div>
          </div>
        ))}
      </div>
      {done ? (
        <div className="client-done-msg">
          <div className="client-done-icon">✓</div>
          <div>
            <div style={{fontWeight:700,fontSize:15,marginBottom:4}}>Your report is ready</div>
            <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:12}}>It has also been sent to your email address.</div>
            <a className="btn btn-primary" href={`/clients/${slug}`}>View Report →</a>
          </div>
        </div>
      ) : (
        <div className="client-wait-msg">
          Your report will be emailed to you when the assessment is complete.
        </div>
      )}
    </div>
  );
}

// ── Reports and Assessments chooser (v3.0) ────────────────────────────────────

const OPTIONS = [
  {
    id: 'it_report', icon: 'ti-shield-check', title: 'IT and Cyber Security Report',
    desc: 'A summary of a client\'s IT and cyber security health, built from the monitoring we already run. Ready in a few seconds.',
    who: 'Existing clients',
  },
  {
    id: 'analytics', icon: 'ti-chart-line', title: 'Website Analytics Report',
    desc: 'Visits, enquiries, where visitors came from and what to fix on the client\'s website, sent as an email.',
    who: 'Clients with website tracking',
  },
  {
    id: 'assessment', icon: 'ti-radar', title: 'External Cyber Security Assessment',
    desc: 'A live scan of what an attacker can see from the internet: domains, email, websites and office connections. Takes 30 to 90 minutes.',
    who: 'Prospects and clients',
  },
];

const fmtUpdated = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return 'Updated ' + d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT';
};

const muted = { fontSize: 12, color: 'var(--text-muted)' };
const subHead = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 };

function OptionCard({ opt, selected, onSelect }) {
  return (
    <button type="button" className="btn-card" aria-pressed={selected} onClick={() => onSelect(opt.id)} style={{
      display: 'flex', flexDirection: 'column', gap: 6, minHeight: 150, padding: '16px 18px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className={`ti ${opt.icon}`} style={{ fontSize: 20, color: selected ? 'var(--accent)' : 'var(--text-muted)' }} />
        <span style={{ fontSize: 14, fontWeight: 700 }}>{opt.title}</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, flex: 1 }}>{opt.desc}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: selected ? 'var(--accent)' : 'var(--text-muted)' }}>{opt.who}</div>
    </button>
  );
}

function ClientPicker({ clients, value, onChange, filter }) {
  const list = clients.filter(c => !c.archived && (!filter || filter(c)));
  return (
    <div className="na-field">
      <label>Client</label>
      <select value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Select a client</option>
        {list.map(c => <option key={c.slug} value={c.slug}>{c.display_name}</option>)}
      </select>
    </div>
  );
}

function SourceList({ sources }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {sources.map(s => (
        <div key={s.key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12 }}>
          <span style={{ color: s.available ? 'var(--green)' : 'var(--text-dim)', width: 14, flexShrink: 0 }}>{s.available ? '✓' : '–'}</span>
          <span style={{ color: s.available ? 'var(--text)' : 'var(--text-muted)', minWidth: 170 }}>{s.label}</span>
          <span style={muted}>{s.available ? [s.detail, fmtUpdated(s.updated)].filter(Boolean).join(' · ') : 'No data'}</span>
        </div>
      ))}
    </div>
  );
}

function ItReportPanel({ clients, initialSlug, isAdmin }) {
  const [slug, setSlug]       = useState(initialSlug || '');
  const [sendOpen, setSendOpen] = useState(false);
  const [ready, setReady]     = useState(null);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState(null);
  const [latest, setLatest]   = useState(null);

  useEffect(() => {
    setReady(null); setMsg(null); setLatest(null);
    if (!slug) return;
    authFetch(`/api/clients/${slug}/report-readiness`).then(r => r.ok ? r.json() : null).then(d => {
      setReady(d); setLatest(d?.it_report?.last_report || null);
    }).catch(() => {});
  }, [slug]);

  const generate = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await authFetch(`/api/clients/${slug}/report/generate`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail ? `${d.error}: ${String(d.detail).slice(-300)}` : (d.error || 'Report generation failed'));
      const rr = await authFetch(`/api/clients/${slug}/report-readiness`).then(x => x.json()).catch(() => null);
      setLatest(rr?.it_report?.last_report || null);
      setMsg({ ok: true, text: 'Report generated. It has not been emailed: open it to review, then use Email to client.' });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally { setBusy(false); }
  };

  const view = () => {
    const token = localStorage.getItem('stg_token');
    window.open(`/api/clients/${slug}/reports/${latest.filename}/view?token=${token}`, '_blank');
  };

  const download = async (internal) => {
    const filename = internal ? latest.filename.replace('_client_report.html', '_client_report_internal.html') : latest.filename;
    try {
      const res = await authFetch(`/api/clients/${slug}/reports/${encodeURIComponent(filename)}`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Download failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    }
  };

  const it = ready?.it_report;
  const nothing = it && !it.sources.some(s => s.available);
  return (
    <div className="na-form">
      <ClientPicker clients={clients} value={slug} onChange={setSlug} />
      {slug && !ready && <div style={muted}><span className="spinner" /> Checking what data is available…</div>}
      {it && (
        <div>
          <div style={subHead}>This report will include</div>
          <SourceList sources={it.sources} />
          {it.sections_off.length > 0 && (
            <div style={{ ...muted, marginTop: 8 }}>{it.sections_off.length} section{it.sections_off.length !== 1 ? 's are' : ' is'} switched off for this client in its report settings.</div>
          )}
          {nothing && <div className="error-msg" style={{ marginTop: 10 }}>There is no monitoring data for this client yet, so the report would be almost empty.</div>}
          {latest && <div style={{ ...muted, marginTop: 10 }}>Last report: {fmtUpdated(latest.generated).replace('Updated ', '')}</div>}
        </div>
      )}
      {msg && <div className={msg.ok ? 'success-msg' : 'error-msg'}>{msg.text}</div>}
      <div className="na-actions">
        <button className="btn btn-primary" type="button" onClick={generate} disabled={!slug || !ready || busy}>
          {busy ? <span className="spinner" /> : 'Generate Report'}
        </button>
        {latest && <button className="btn btn-secondary" type="button" onClick={view}>Open latest report</button>}
        {latest && <button className="btn btn-secondary" type="button" onClick={() => download(false)}>Download</button>}
        {latest && isAdmin && <button className="btn btn-secondary" type="button" onClick={() => download(true)}>Download internal</button>}
        {latest && <button className="btn btn-secondary" type="button" onClick={() => setSendOpen(true)}>Email to client</button>}
      </div>
      {sendOpen && <SendReportDialog slug={slug} kind="it" onClose={() => setSendOpen(false)} />}
    </div>
  );
}

function AnalyticsPanel({ clients, initialSlug }) {
  const [slug, setSlug]     = useState(initialSlug || '');
  const [sendOpen, setSendOpen] = useState(false);
  const [ready, setReady]   = useState(null);
  const [freq, setFreq]     = useState('monthly');
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy]     = useState('');
  const [msg, setMsg]       = useState(null);

  useEffect(() => {
    setReady(null); setMsg(null);
    if (!slug) return;
    authFetch(`/api/clients/${slug}/report-readiness`).then(r => r.ok ? r.json() : null).then(d => {
      setReady(d);
      if (d?.analytics?.schedule?.frequency) setFreq(d.analytics.schedule.frequency);
    }).catch(() => {});
  }, [slug]);

  const a = ready?.analytics;
  const recipients = a?.schedule?.recipients?.length ? a.schedule.recipients : (a?.default_recipient ? [a.default_recipient] : []);

  const buildReport = async (kind) => {
    setBusy(kind); setMsg(null);
    const r = await authFetch(`/api/clients/${slug}/analytics/report/preview?frequency=${freq}`);
    const d = await r.json().catch(() => ({}));
    setBusy('');
    if (!d.html) { setMsg({ ok: false, text: 'Couldn\'t build the report.' }); return null; }
    return d;
  };

  const preview = async () => {
    const d = await buildReport('preview');
    if (!d) return;
    const w = window.open('', '_blank');
    if (w) { w.document.open(); w.document.write(d.html); w.document.title = d.subject || 'Website Analytics Report'; w.document.close(); }
  };

  // v3.5: download the report as an HTML file without emailing anyone
  const download = async () => {
    const d = await buildReport('download');
    if (!d) return;
    const name = (clients.find(c => c.slug === slug)?.display_name || slug).replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const blob = new Blob([d.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${name}_${today}_website_analytics_${freq === 'weekly' ? '7' : '30'}_days.html`;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const send = async (to) => {
    setBusy(to ? 'test' : 'send'); setMsg(null);
    const r = await authFetch(`/api/clients/${slug}/analytics/report/send`, { method: 'POST', body: JSON.stringify({ to, frequency: freq }) });
    const d = await r.json().catch(() => ({}));
    setBusy('');
    setMsg({ ok: r.ok, text: r.ok ? d.message : (d.error || 'Couldn\'t send.') });
  };

  return (
    <div className="na-form">
      <ClientPicker clients={clients} value={slug} onChange={setSlug} />
      {slug && !ready && <div style={muted}><span className="spinner" /> Checking website tracking…</div>}
      {a && !a.tracking && (
        <div className="error-msg">This client has no website tracking data yet. Install the tracking snippet from their Digital Hub first.</div>
      )}
      {a && a.tracking && (
        <>
          <div>
            <div style={subHead}>This report will include</div>
            <div style={{ fontSize: 12, color: 'var(--text)' }}>
              Visits, enquiries, highlights, traffic sources, most viewed pages and what to fix next.
            </div>
            <div style={{ ...muted, marginTop: 4 }}>
              Website data on {a.days_with_data_30} of the last 30 days{a.last_data ? ` · latest ${a.last_data}` : ''}.
            </div>
          </div>
          <div className="na-field">
            <label>Period</label>
            <select value={freq} onChange={e => setFreq(e.target.value)}>
              <option value="monthly">Last 30 days</option>
              <option value="weekly">Last 7 days</option>
            </select>
          </div>
          <div>
            <div style={subHead}>Recipients</div>
            <div style={{ fontSize: 12 }}>{recipients.length ? recipients.join(', ') : <span style={{ color: 'var(--red)' }}>No recipients — add a contact email or set up a schedule.</span>}</div>
            <div style={{ ...muted, marginTop: 4 }}>
              {a.schedule
                ? <>Scheduled {a.schedule.frequency}{a.schedule.enabled !== false && a.schedule.next_run_local ? `, next ${a.schedule.next_run_local}` : ' (paused)'}. Change it on the <a href="/schedules" style={{ color: 'var(--accent)' }}>Schedules</a> page.</>
                : <>Not scheduled. Set up a regular send on the <a href="/schedules" style={{ color: 'var(--accent)' }}>Schedules</a> page.</>}
            </div>
          </div>
        </>
      )}
      {msg && <div className={msg.ok ? 'success-msg' : 'error-msg'}>{msg.text}</div>}
      <div className="na-actions">
        <button className="btn btn-secondary" type="button" onClick={preview} disabled={!a?.tracking || !!busy}>{busy === 'preview' ? <span className="spinner" /> : 'Preview'}</button>
        <button className="btn btn-secondary" type="button" onClick={download} disabled={!a?.tracking || !!busy}>{busy === 'download' ? <span className="spinner" /> : 'Download'}</button>
        <button className="btn btn-primary" type="button" onClick={() => setSendOpen(true)} disabled={!a?.tracking || !recipients.length || !!busy}>Email to client</button>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="Send a test to…" style={{ width: 190 }} disabled={!a?.tracking} />
          <button className="btn btn-secondary" type="button" onClick={() => send(testTo.trim())} disabled={!a?.tracking || !testTo.trim() || !!busy}>{busy === 'test' ? <span className="spinner" /> : 'Send test'}</button>
        </span>
      </div>
      {sendOpen && <SendReportDialog slug={slug} kind="analytics" onClose={() => setSendOpen(false)} />}
    </div>
  );
}

function AssessmentForm({ clients, isAdmin, clientName, setClientName, domains, setDomains, onSubmit, error, running, initialSlug }) {
  const [target, setTarget]       = useState(isAdmin ? 'client' : 'prospect');
  const [slug, setSlug]           = useState(initialSlug || '');
  const [ready, setReady]         = useState(null);
  const [disclaimer, setDisclaimer] = useState(false);

  useEffect(() => {
    setReady(null);
    if (target !== 'client' || !slug) return;
    const c = clients.find(x => x.slug === slug);
    setClientName(c?.display_name || '');
    setDomains(c?.meta?.domains || '');
    authFetch(`/api/clients/${slug}/report-readiness`).then(r => r.ok ? r.json() : null).then(setReady).catch(() => {});
  }, [slug, target, clients.length]); // eslint-disable-line

  const switchTarget = (t) => { setTarget(t); setSlug(''); setClientName(isAdmin ? '' : clientName); setDomains(''); setReady(null); };
  const office = ready?.assessment?.office_ips || [];
  const last = ready?.assessment?.last_assessment;

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(target === 'client' ? slug : null); }} className="na-form">
      {isAdmin && (
        <div className="view-toggle" style={{ alignSelf: 'flex-start' }}>
          <button type="button" className={`view-toggle-btn${target === 'client' ? ' active' : ''}`} onClick={() => switchTarget('client')}>Existing client</button>
          <button type="button" className={`view-toggle-btn${target === 'prospect' ? ' active' : ''}`} onClick={() => switchTarget('prospect')}>New prospect</button>
        </div>
      )}
      {isAdmin && target === 'client' ? (
        <ClientPicker clients={clients} value={slug} onChange={setSlug} />
      ) : isAdmin ? (
        <div className="na-field">
          <label>Company name</label>
          <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Acme Corporation" required disabled={running} />
          <div className="field-hint">Used for report naming and folder creation</div>
        </div>
      ) : (
        <div className="na-field">
          <label>Client</label>
          <input type="text" value={clientName} readOnly style={{ cursor: 'default' }} />
          <div className="field-hint">Contact STG to update your company name</div>
        </div>
      )}
      <div className="na-field">
        <label>Domains</label>
        <input type="text" value={domains} onChange={e => setDomains(e.target.value)} placeholder="acme.com,acme.co.uk" required disabled={running} />
        <div className="field-hint">
          {target === 'client' && isAdmin ? 'Taken from the client\'s record. Change it here for this run only, or edit the client to change it permanently.' : 'Comma-separated. Include all primary domains.'}
        </div>
      </div>
      {isAdmin && target === 'client' && slug && ready && (
        <div>
          <div style={subHead}>Office connections to be port scanned</div>
          {office.length
            ? office.map(o => <div key={o.ip} style={{ fontSize: 12 }}><span style={{ fontFamily: 'var(--font-mono)' }}>{o.ip}</span> <span style={muted}>· {o.site} ({o.source})</span></div>)
            : <div style={muted}>None recorded. Add the office public IP to the client's sites to include it.</div>}
          {last && <div style={{ ...muted, marginTop: 8 }}>Last assessment: {fmtUpdated(last.generated).replace('Updated ', '')}</div>}
        </div>
      )}
      <div className="na-disclaimer">
        <label className="na-disclaimer-label">
          <input type="checkbox" checked={disclaimer} onChange={e => setDisclaimer(e.target.checked)} required />
          <span>
            I confirm that I have <strong>written authority</strong> to conduct security scanning and penetration testing against the domains{office.length ? ' and office connections' : ''} listed above. I understand that unauthorised computer access is a criminal offence under the <strong>Computer Misuse Act 1990</strong> and related legislation. I accept full legal responsibility for ensuring this assessment is properly authorised.
          </span>
        </label>
      </div>
      {error && <div className="error-msg">{error}</div>}
      <div className="na-actions">
        <button className="btn btn-primary" type="submit" disabled={running || !disclaimer || !domains.trim() || (isAdmin && target === 'client' ? !slug : !clientName.trim())}>Run Assessment</button>
      </div>
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NewAssessment() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const typeParam = searchParams.get('type');
  // v3.1: ?for=<slug> chooses the client (the Generate button on each report section)
  const forSlug = isAdmin ? (searchParams.get('for') || '') : '';
  const [choice, setChoice] = useState(isAdmin ? (OPTIONS.some(o => o.id === typeParam) ? typeParam : '') : 'assessment');
  const [clients, setClients] = useState([]);

  const clientDisplayName = user?.client_slug ? user.client_slug.replace(/_/g, ' ') : '';
  const [clientName, setClientName] = useState(isAdmin ? '' : clientDisplayName);
  const [domains, setDomains]       = useState('');
  const [running, setRunning]       = useState(false);
  const [done, setDone]             = useState(false);
  const [error, setError]           = useState('');
  const [slug, setSlug]             = useState('');
  const [lines, setLines]           = useState([]);
  const [viewMode, setViewMode]     = useState('progress');
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling]       = useState(false);
  const logRef = useRef(null);
  const esRef  = useRef(null);

  useEffect(() => {
    if (!isAdmin) return;
    authFetch('/api/clients').then(r => r.json())
      .then(d => setClients((Array.isArray(d) ? d : []).sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    const incomingSlug   = searchParams.get('slug');
    const incomingClient = searchParams.get('client');
    if (incomingSlug && incomingClient) {
      setChoice('assessment');
      setSlug(incomingSlug);
      setClientName(incomingClient);
      const token = localStorage.getItem('stg_token');
      fetch(`/api/assessments/${incomingSlug}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()).then(data => {
        if (data.status !== 'unknown' && !data.done) {
          setRunning(true);
          startStream(incomingSlug, false);
        }
      }).catch(() => {});
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (viewMode === 'console' && logRef.current)
      logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [lines, viewMode]);

  useEffect(() => {
    return () => { if (esRef.current) esRef.current.close(); };
  }, []);

  const startStream = async (slug, fresh = true) => {
    const sseToken = await getSseToken();
    const freshParam = fresh ? '&fresh=1' : '';
    const es = new EventSource(`/api/assessments/${slug}/logs?token=${sseToken}${freshParam}`);
    esRef.current = es;
    es.onmessage = (e) => {
      const data = e.data;
      if (data === '[DONE]') {
        es.close(); setRunning(false);
        authFetch(`/api/assessments/${slug}/status`)
          .then(r => r.json())
          .then(d => {
            if (d.exit_code !== null && d.exit_code !== 0) {
              setError('Assessment completed with errors — report may not have been generated. Check the console output above for details.');
            }
            setDone(true);
          })
          .catch(() => setDone(true));
      } else {
        setLines(prev => [...prev, data]);
      }
    };
    es.onerror = () => {
      setLines(prev => [...prev, '[connection lost — assessment may still be running]']);
      es.close();
    };
  };

  // existingSlug: an existing client (office IPs are added by the portal);
  // null: a prospect or a client user's own assessment.
  const handleSubmit = async (existingSlug) => {
    setError(''); setLines([]); setDone(false); setRunning(true);
    try {
      const res = existingSlug
        ? await authFetch(`/api/clients/${existingSlug}/assessment/run`, { method: 'POST', body: JSON.stringify({ domains: domains.trim() }) })
        : await authFetch('/api/assessments', { method: 'POST', body: JSON.stringify({ client_name: clientName.trim(), domains: domains.trim() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start assessment');
      if (isAdmin) {
        navigate('/active');
      } else {
        setSlug(data.slug);
        setRunning(true);
        startStream(data.slug, true);
      }
    } catch (err) {
      setError(err.message); setRunning(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelConfirm) { setCancelConfirm(true); return; }
    setCancelling(true);
    setCancelConfirm(false);
    try {
      await authFetch(`/api/assessments/${slug}/cancel`, { method: 'POST' });
    } catch { /* ignore */ }
    setCancelling(false);
    setRunning(false);
    setDone(false);
    if (esRef.current) esRef.current.close();
  };

  const handleReset = () => {
    setClientName(''); setDomains(''); setRunning(false);
    setDone(false); setError(''); setSlug(''); setLines([]);
    setCancelConfirm(false);
    if (esRef.current) esRef.current.close();
  };

  const classifyLine = (line) => {
    const l = line.toLowerCase();
    if (l.includes('error') || l.includes('fail') || l.includes('denied')) return 'log-error';
    if (l.includes('warn')) return 'log-warn';
    if (l.includes('✓') || l.includes('done') || l.includes('complete') || l.includes('success')) return 'log-success';
    if (l.startsWith('[*]') || l.startsWith('=')) return 'log-info';
    return 'log-default';
  };

  const showProgress = running || done || lines.length > 0;
  const selected = OPTIONS.find(o => o.id === choice);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">{isAdmin ? 'Reports and Assessments' : 'External Cyber Security Assessment'}</div>
        <div className="page-subtitle">
          {isAdmin ? 'Produce a client report on demand, or run a live external scan' : 'Run a live scan of what an attacker can see from the internet'}
        </div>
      </div>

      {isAdmin && !showProgress && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20, maxWidth: 900 }}>
          {OPTIONS.map(o => <OptionCard key={o.id} opt={o} selected={choice === o.id} onSelect={setChoice} />)}
        </div>
      )}

      {!showProgress && selected && (
        <div className="card na-form-card">
          {isAdmin && <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 18 }}>{selected.title}</div>}
          {choice === 'it_report'  && <ItReportPanel clients={clients} initialSlug={forSlug} isAdmin={isAdmin} />}
          {choice === 'analytics'  && <AnalyticsPanel clients={clients} initialSlug={forSlug} />}
          {choice === 'assessment' && (
            <AssessmentForm clients={clients} isAdmin={isAdmin} initialSlug={forSlug} clientName={clientName} setClientName={setClientName}
              domains={domains} setDomains={setDomains} onSubmit={handleSubmit} error={error} running={running} />
          )}
        </div>
      )}

      {showProgress && (
        <div className="na-log-wrap">
          <div className="na-log-header">
            <div className="na-log-title">
              {isAdmin && (
                <>
                  {running && <><span className="pulse-dot" /> {clientName}</>}
                  {done && <><span className="done-dot" /> {clientName}</>}
                </>
              )}
            </div>
            <div className="na-log-actions">
              {isAdmin && (
                <div className="view-toggle">
                  <button className={`view-toggle-btn${viewMode === 'progress' ? ' active' : ''}`}
                    onClick={() => setViewMode('progress')}>Progress</button>
                  <button className={`view-toggle-btn${viewMode === 'console' ? ' active' : ''}`}
                    onClick={() => setViewMode('console')}>Console</button>
                </div>
              )}
              {running && !done && isAdmin && (
                cancelConfirm ? (
                  <>
                    <button className="btn btn-danger"
                      disabled={cancelling} onClick={handleCancel}>
                      {cancelling ? <span className="spinner" /> : 'Confirm cancel'}
                    </button>
                    <button className="btn btn-secondary"
                      onClick={() => setCancelConfirm(false)}>Keep running</button>
                  </>
                ) : (
                  <button className="btn btn-secondary"
                    onClick={handleCancel}>Cancel scan</button>
                )
              )}
              {done && isAdmin && (
                <>
                  <a className="btn btn-secondary" href={`/clients/${slug}`}>View Reports →</a>
                  <button className="btn btn-secondary" onClick={handleReset}>New Report or Assessment</button>
                </>
              )}
            </div>
          </div>

          {(viewMode === 'progress' || !isAdmin) && (
            isAdmin
              ? <AdminProgress lines={lines} done={done} clientName={clientName} />
              : <ClientProgress lines={lines} done={done} clientName={clientName} slug={slug} />
          )}

          {viewMode === 'console' && isAdmin && (
            <div className="na-log" ref={logRef}>
              {lines.map((line, i) => (
                <div key={i} className={`log-line ${classifyLine(line)}`}>
                  <span className="log-num">{String(i+1).padStart(4,'0')}</span>
                  <span className="log-text">{line || ' '}</span>
                </div>
              ))}
              {running && <div className="log-line log-cursor"><span className="log-num">    </span><span className="log-text">▌</span></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
